/**
 * «Я здесь» on a map (INV-PH-140): a round button of the map's own controls. A tap asks the browser for the position
 * (`getCurrentPosition`, by the tap — `docs/UI_CONTRACT.md` §4.2), draws a dot with the accuracy circle and moves the
 * camera there. It sends nothing to the guide and asks no API — tiles only: the position stays in the browser. The
 * visitor's place for the GUIDE is the card's «Определить моё место»; this is only «where am I on this map».
 */

import type * as MapGL from '@2gis/mapgl/types';
import type { Map as MapGLMap } from '@2gis/mapgl/types';

/** Closest the camera comes to the visitor's dot: a street, not a roof. */
export const LOCATE_ZOOM = 16;
const ACCURACY_MIN_M = 5;

export const LOCATE_LABEL = 'Я здесь';

/** The dot: a disc in the accent colour with a white ring, the centre of an HtmlMarker. */
export function locateDotHtml(): string {
  return '<span class="map-locate-dot" aria-hidden="true"></span>';
}

export function locateButtonHtml(failed: boolean): string {
  return `<button type="button" class="map-locate-btn${failed ? ' map-locate-btn--failed' : ''}" aria-label="${LOCATE_LABEL}" title="${failed ? 'Не удалось определить место' : LOCATE_LABEL}"><span class="map-locate-btn__icon" aria-hidden="true"></span></button>`;
}

type Geo = Pick<Geolocation, 'getCurrentPosition'>;

/**
 * Adds the button. → a function that removes it, with the dot and the circle.
 * Every object is created on a tap and destroyed with the control: a map that outlives its card (`mapHost`) is left clean.
 */
export function mountLocateControl(
  sdk: typeof MapGL,
  map: MapGLMap,
  opts: { geolocation?: Geo; position?: MapGL.ControlPosition; accent?: string } = {},
): () => void {
  const geolocation = opts.geolocation ?? (typeof navigator === 'undefined' ? undefined : navigator.geolocation);
  const control = new sdk.Control(map, locateButtonHtml(false), { position: opts.position ?? 'centerRight' });
  let dot: MapGL.HtmlMarker | null = null;
  let circle: MapGL.Circle | null = null;
  let alive = true;
  let busy = false;

  const clear = () => { dot?.destroy(); circle?.destroy(); dot = null; circle = null; };
  const button = () => control.getContainer().querySelector('button');

  const onClick = () => {
    if (!geolocation || busy) return;
    busy = true;
    button()?.classList.add('map-locate-btn--busy');
    geolocation.getCurrentPosition(
      (position) => {
        busy = false;
        if (!alive) return;
        button()?.classList.remove('map-locate-btn--busy', 'map-locate-btn--failed');
        const at: number[] = [position.coords.longitude, position.coords.latitude];
        const radius = Math.max(ACCURACY_MIN_M, position.coords.accuracy || 0);
        clear();
        const accent = opts.accent ?? '#1769aa';
        circle = new sdk.Circle(map, { coordinates: at, radius, color: `${accent}22`, strokeColor: `${accent}66`, strokeWidth: 1, zIndex: 10 });
        dot = new sdk.HtmlMarker(map, { coordinates: at, html: locateDotHtml(), anchor: [9, 9], zIndex: 70, preventMapInteractions: false });
        map.setCenter(at, { duration: 350 });
        map.setZoom(Math.max(map.getZoom(), LOCATE_ZOOM), { duration: 350 });
      },
      () => {
        busy = false;
        if (!alive) return;
        const el = button();
        el?.classList.remove('map-locate-btn--busy');
        el?.classList.add('map-locate-btn--failed');
        el?.setAttribute('title', 'Не удалось определить место');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  };
  control.getContainer().addEventListener('click', onClick);
  return () => {
    alive = false;
    control.getContainer().removeEventListener('click', onClick);
    clear();
    control.destroy();
  };
}
